import React, {ReactElement, ReactNode, useMemo} from 'react';
import {useBoolean} from '../../Hooks';
import {RsnDialog} from '../RsnDialog/RsnDialog';
import {
    fallbackIcon,
    HintType as HintTypeT,
    OptionObject as OptionObjectT,
    propertyAsKey,
    selfAsKey,
} from './RsnPicker.shared';
import {RsnPickerDialog} from './RsnPickerDialog';
import {RsnPickerTrigger} from './RsnPickerTrigger';

export interface RsnPickerProps<T> {
    title?: string;
    label?: string | ReactElement;
    value?: T | null;
    onChange: (value: T) => void;
    options: OptionObjectT<T>[];
    disabled?: boolean;
    keyedBy?: keyof T;
    placeholderText?: string;
    empty?: ReactNode;
    footer?: ReactNode;
}

export namespace RsnPicker {
    export type Props<T> = RsnPickerProps<T>;
    export type OptionObject<T> = OptionObjectT<T>;
    export type HintType = HintTypeT;
}

export function RsnPicker<T>({
    title = 'Select option',
    label,
    options,
    disabled,
    value,
    onChange,
    keyedBy,
    placeholderText = 'Select option',
    empty = 'No options',
    footer,
}: RsnPickerProps<T>) {
    const [isOpen, controlDialog] = useBoolean(false);

    const valueAsKey = keyedBy ? propertyAsKey(keyedBy) : selfAsKey<T>();
    const matchingOption = useMemo(
        () => (value == null ? null : options.find(opt => valueAsKey(opt.value) === valueAsKey(value))),
        [value, options, keyedBy]
    );
    const selectedOption: OptionObjectT<T | undefined> = matchingOption ?? {
        value: undefined,
        icon: fallbackIcon,
        title: placeholderText,
    };

    const handleChange = (value: T) => {
        onChange(value);
        controlDialog.off();
    };

    return (
        <>
            <div className={'gap flex flex-col gap-2'}>
                {label && <label className={'fv-text-m-regular text-neutral-100'}>{label}</label>}
                <RsnPickerTrigger option={selectedOption} onClick={controlDialog.on} disabled={disabled} />
            </div>

            <RsnDialog.Controller onClose={controlDialog.off}>
                {isOpen && (
                    <RsnPickerDialog
                        onChange={handleChange}
                        options={options}
                        value={value}
                        keyedBy={keyedBy}
                        footer={footer}
                        title={title}
                        empty={empty}
                    />
                )}
            </RsnDialog.Controller>
        </>
    );
}

RsnPicker.Dialog = RsnPickerDialog;
RsnPicker.Trigger = RsnPickerTrigger;
